/**
 * Editor Pop-Out Panel Content
 * 
 * Control strategy code and configuration.
 * Per spec:
 * - File tree (strategy code, helpers, notes)
 * - Parameters
 * - Strategy metadata
 * - Validation rules
 * - Data configuration (fees, capital, etc.)
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFile,
    faFolder,
    faCog,
    faSliders,
    faDatabase,
} from '@fortawesome/free-solid-svg-icons';

interface EditorPopOutProps {
    onClose: () => void;
}

export const EditorPopOut: React.FC<EditorPopOutProps> = ({ onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Check if we're in a project context
    const projectMatch = location.pathname.match(/\/app\/lab\/project\/([^/]+)/);
    const projectId = projectMatch ? projectMatch[1] : null;

    const handleNavigate = (path: string) => {
        navigate(path);
        onClose();
    };

    if (!projectId) {
        return (
            <div className="p-4">
                <div className="text-center py-8">
                    <FontAwesomeIcon icon={faFolder} className="h-8 w-8 text-gray-600 mb-3" />
                    <p className="text-sm text-gray-400 mb-2">No project open</p>
                    <p className="text-xs text-gray-500">
                        Open a project to access the editor
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 space-y-6">
            {/* File Tree Section */}
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faFile} className="h-3.5 w-3.5 text-white" />
                    <h3 className="text-xs font-medium text-white uppercase tracking-wider">Files</h3>
                </div>
                <div className="space-y-1">
                    <button
                        onClick={() => handleNavigate(`/app/lab/project/${projectId}`)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors text-left"
                    >
                        <FontAwesomeIcon icon={faFile} className="h-4 w-4 text-cyan-500/70" />
                        <span className="text-sm text-gray-300">main.ts</span>
                    </button>
                </div>
            </div>

            {/* Parameters Section */}
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faSliders} className="h-3.5 w-3.5 text-white" />
                    <h3 className="text-xs font-medium text-white uppercase tracking-wider">Parameters</h3>
                </div>
                <button
                    onClick={() => handleNavigate(`/app/lab/project/${projectId}/params`)}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors text-left"
                >
                    <span className="text-sm text-gray-300">Edit Parameters</span>
                </button>
            </div>

            {/* Configuration Section */}
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faCog} className="h-3.5 w-3.5 text-white" />
                    <h3 className="text-xs font-medium text-white uppercase tracking-wider">Configuration</h3>
                </div>
                <div className="space-y-1">
                    <button
                        onClick={() => handleNavigate(`/app/lab/project/${projectId}/config`)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors text-left"
                    >
                        <FontAwesomeIcon icon={faDatabase} className="h-4 w-4 text-white" />
                        <span className="text-sm text-gray-300">Data & Fees</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditorPopOut;
